/**
 * Axis ticks for the small inline charts (box plot, regression scatter plot).
 *
 * Heckbert's "nice numbers": the step is 1, 2, 2.5 or 5 times a power of ten, and the
 * axis is widened outward to whole steps so the first and last tick bracket the data.
 */

export interface AxisTicks {
  /** The axis minimum: the first tick, at or below the data minimum. */
  lo: number;
  /** The axis maximum: the last tick, at or above the data maximum. */
  hi: number;
  ticks: number[];
}

function niceStep(rawStep: number): number {
  const exponent = Math.floor(Math.log10(rawStep));
  const base = 10 ** exponent;
  const fraction = rawStep / base;
  if (fraction <= 1) {
    return base;
  }
  if (fraction <= 2) {
    return 2 * base;
  }
  if (fraction <= 2.5) {
    return 2.5 * base;
  }
  if (fraction <= 5) {
    return 5 * base;
  }
  return 10 * base;
}

/** Strip binary-fraction noise: 0.30000000000000004 → 0.3. */
function clean(value: number): number {
  return Number.parseFloat(value.toPrecision(12));
}

export function niceTicks(min: number, max: number, target = 5): AxisTicks {
  if (!(Number.isFinite(min) && Number.isFinite(max))) {
    return { lo: 0, hi: 1, ticks: [0, 1] };
  }
  let low = Math.min(min, max);
  let high = Math.max(min, max);
  if (low === high) {
    const pad = Math.abs(low) * 0.1 || 1;
    low -= pad;
    high += pad;
  }
  const step = niceStep((high - low) / Math.max(1, target - 1));
  const first = Math.floor(clean(low / step));
  const last = Math.ceil(clean(high / step));
  const ticks: number[] = [];
  for (let i = first; i <= last; i++) {
    ticks.push(clean(i * step));
  }
  return { lo: clean(first * step), hi: clean(last * step), ticks };
}
